import { supabase } from './client';
import { EarlyWarningData } from '@/types/early-warning';
import { logError } from '@/utils/logger';

export async function fetchEarlyWarnings() {
  const { data, error } = await supabase
    .from('early_warning_register')
    .select('*')
    .order('date_saved', { ascending: false });

  if (error) {
    logError('Error fetching early warnings:', error);
    throw new Error('Failed to fetch early warnings');
  }

  return data;
}

export async function saveEarlyWarning(
  projectName: string,
  data: EarlyWarningData
): Promise<void> {
  // Validate required data
  if (!projectName?.trim()) {
    throw new Error('Project name is required');
  }

  if (!data || typeof data !== 'object') {
    throw new Error('Invalid early warning data');
  }

  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    throw new Error('User must be authenticated to save early warnings');
  }

  // Prepare data for saving
  const saveData = {
    ...data,
    projectName: data.projectName || projectName,
    clientName: data.clientName || '',
  };

  const { error } = await supabase.from('early_warning_register').insert({ 
    project_name: projectName,
    data: saveData,
    user_id: user.id,
  });

  if (error) {
    logError('Error saving early warning:', error);
    throw new Error('Failed to save early warning');
  }
}

export async function updateEarlyWarning(
  id: string,
  updates: Partial<EarlyWarningData>
): Promise<void> {
  const { error } = await supabase
    .from('early_warning_register')
    .update({ data: updates })
    .eq('id', id);

  if (error) {
    logError('Error updating early warning:', error);
    throw new Error('Failed to update early warning');
  }
}

export async function deleteEarlyWarning(id: string): Promise<void> {
  const { error } = await supabase
    .from('early_warning_register')
    .delete()
    .eq('id', id);

  if (error) {
    logError('Error deleting early warning:', error);
    throw new Error('Failed to delete early warning');
  }
}

export async function renameEarlyWarning(
  id: string,
  newName: string
): Promise<void> {
  if (!id) {
    throw new Error('Early warning ID is required');
  }

  if (!newName.trim()) {
    throw new Error('New name is required');
  }

  const { error } = await supabase
    .from('early_warning_register')
    .update({ project_name: newName.trim() })
    .eq('id', id);

  if (error) {
    logError('Error renaming early warning:', error);
    throw new Error('Failed to rename early warning');
  }
}